import { useState, useEffect, useCallback } from 'react';
import { activitiesAPI, teamAPI, leadsAPI } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';
import { getActivityTypeConfig, getActivityIcon } from '@/lib/activity-types';
import { Activity, ChevronLeft, ChevronRight, User, Plus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

export default function ActivitiesPage() {
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [showAdd, setShowAdd] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const navigate = useNavigate();
  const { settings, formatDateTime } = useSettings();
  const activityTypes = (settings.activity_types || []).map(t => typeof t === 'object' ? t : { key: t, label: t });

  const fetchActivities = useCallback(async () => {
    try {
      const params = { 
        skip: (page - 1) * limit,
        limit: limit 
      };
      if (typeFilter) params.type = typeFilter;
      const res = await activitiesAPI.list(params);
      setActivities(res.data.activities);
      setTotal(res.data.total);
    } catch {} finally { setLoading(false); }
  }, [typeFilter, page, limit]);

  useEffect(() => {
    setPage(1);
  }, [typeFilter, assigneeFilter]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  useEffect(() => {
    teamAPI.members().then(res => setTeamMembers(res.data || [])).catch(() => setTeamMembers([]));
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  const totalPages = Math.ceil(total / limit);
  const hasNextPage = page < totalPages;
  const hasPrevPage = page > 1;

  return (
    <div className="space-y-5 fade-in" data-testid="activities-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Activiteiten</h1>
          <p className="text-muted-foreground text-sm">{total} total interactions</p>
        </div>
        <div className="flex items-center gap-2">
          {teamMembers.length > 0 && (
            <select value={assigneeFilter || 'all'} onChange={(e) => setAssigneeFilter(e.target.value === 'all' ? '' : e.target.value)} className="flex h-9 w-[160px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
              <option value="all">Alle leden</option>
              {teamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
            </select>
          )}
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value === 'all' ? '' : e.target.value)} data-testid="activity-type-filter" className="flex h-9 w-[160px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
            <option value="all">All Types</option>
            {activityTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <Button size="sm" className="gap-1.5" onClick={() => setShowAdd(true)}>
            <Plus className="w-4 h-4" /> Nieuw
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {activities.map((act) => {
          const cfg = getActivityTypeConfig(act.type);
          const Icon = cfg.icon;
          return (
            <Card key={act.id} className="border-border/60 hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate(`/leads/${act.lead_id}`)} data-testid={`activity-${act.id}`}>
              <CardContent className="p-4 flex items-start gap-4">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cfg.bg}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium capitalize">{act.type}</span>
                    {act.outcome && <Badge variant="secondary" className="text-[10px]">{act.outcome.replace(/_/g, ' ')}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    <span className="font-medium text-foreground">{act.assigned_to_name || act.created_by_name}</span> with <span className="font-medium text-foreground">{act.lead_name}</span>
                  </p>
                  {act.notes && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{act.notes}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {act.assigned_to_name && act.assigned_to_name !== act.created_by_name && (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/20 text-primary text-[10px] font-semibold" title={`Toegewezen aan: ${act.assigned_to_name}`}>
                      {act.assigned_to_name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="text-[10px] mono text-muted-foreground">{formatDateTime(act.created_at)}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {activities.length === 0 && !loading && (
          <div className="text-center py-16 text-muted-foreground">
            <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Nog geen activiteiten geregistreerd</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total} activities
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p - 1)}
              disabled={!hasPrevPage}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">Page {page} of {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={!hasNextPage}
              data-testid="next-page-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <AddActivityDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSuccess={fetchActivities}
        teamMembers={teamMembers}
      />
    </div>
  );
}

function AddActivityDialog({ open, onClose, onSuccess, teamMembers }) {
  const { settings } = useSettings();
  const activityTypes = (settings.activity_types || []).map(t => typeof t === 'object' ? t : { key: t, label: t });
  const defaultType = activityTypes[0]?.key || 'call';

  const [leadId, setLeadId] = useState('');
  const [type, setType] = useState(defaultType);
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingLeads, setLoadingLeads] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLeadId('');
    setType(defaultType);
    setOutcome('');
    setNotes('');
    setAssignedTo('');
    setSearch('');
    setLoadingLeads(true);
    leadsAPI.list({ limit: 500 })
      .then(res => setLeads(res.data.leads || []))
      .catch(() => setLeads([]))
      .finally(() => setLoadingLeads(false));
  }, [open, defaultType]);

  const filteredLeads = leads.filter(l => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (l.name || '').toLowerCase().includes(q) ||
           (l.email || '').toLowerCase().includes(q) ||
           (l.phone || '').toLowerCase().includes(q);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!leadId) { toast.error('Selecteer een lead'); return; }
    setSaving(true);
    try {
      await activitiesAPI.create({
        lead_id: leadId,
        type,
        outcome: outcome || null,
        notes: notes || null,
        assigned_to: assignedTo || null,
      });
      toast.success('Activiteit aangemaakt');
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Aanmaken mislukt';
      toast.error(typeof msg === 'string' ? msg : 'Aanmaken mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="add-activity-dialog">
        <DialogHeader><DialogTitle>Nieuwe activiteit</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Lead</Label>
            <Input
              type="text"
              placeholder="Zoek lead op naam, e-mail of telefoon..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-2"
            />
            <select
              value={leadId || ""}
              onChange={(e) => setLeadId(e.target.value)}
              required
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
            >
              <option value="">{loadingLeads ? 'Leads laden...' : '-- Selecteer een lead --'}</option>
              {filteredLeads.map(l => (
                <option key={l.id} value={l.id}>
                  {l.name}{l.email ? ` (${l.email})` : ''}
                </option>
              ))}
            </select>
            {!loadingLeads && filteredLeads.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">Geen leads gevonden</p>
            )}
          </div>

          <div>
            <Label>Type</Label>
            <select
              value={type || ""}
              onChange={(e) => setType(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
            >
              {activityTypes.length === 0 && <option value="call">call</option>}
              {activityTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <Label>Uitkomst</Label>
            <Input
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              placeholder="bijv. afgerond, voicemail, terugbellen..."
            />
          </div>

          <div>
            <Label>Notities</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optionele notities..."
            />
          </div>

          {teamMembers.length > 0 && (
            <div>
              <Label className="flex items-center gap-1"><User className="w-3 h-3" /> Toewijzen aan</Label>
              <select
                value={assignedTo || ''}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
              >
                <option value="">Automatisch (ik)</option>
                {teamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </select>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Annuleren</Button>
            <Button type="submit" disabled={saving || !leadId}>
              {saving ? 'Opslaan...' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
